import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublicIcon from "@mui/icons-material/Public";
import SmsFailedOutlinedIcon from "@mui/icons-material/SmsFailedOutlined";
import { Box, Chip, Link, Paper, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Utils from "../../utils/Utils";
import PostDirectory from "../PostDirectory";
import User from "../User";
import UserAvatar from "../UserAvatar";
import CommentSection from "./CommentSection";
import LinkPreview from "./LinkPreview";
import ReactionAndComments from "./ReactionAndComments";
import ReportPost from "./ReportPost";

const { postBorderColors, splitText, urlRegex } = Utils;

export default function Post({
  post,
  profile,
  deletePost,
  setNewPostModalOpened,
  setEditingPost,
  interactionDisabled,
  pageType,
}) {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  let links = post.description.match(urlRegex);
  links = links?.map((link) => {
    if (!link.startsWith("http")) {
      return `https://${link}`;
    }
    return link;
  });
  const segments = splitText(post.description);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isCommentSectionOpened, setIsCommentSectionOpened] = useState(false);
  const [isReportPostModal, setIsReportPostModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const editCurrentPost = () => {
    setEditingPost(post);
    setNewPostModalOpened(true);
  };

  return (
    <Paper
      key={post._id}
      elevation={2}
      sx={{
        padding: "1rem",
        my: 2.5,
        borderRadius: 3,
        boxShadow: `3px 3px 10px ${
          postBorderColors[post.isCommercial ? "commercial" : post.type]
        },-3px -3px 10px ${
          postBorderColors[post.isCommercial ? "commercial" : post.type]
        }`,
      }}
    >
      <Box sx={{ display: "flex" }}>
        <UserAvatar
          userId={post.authorID}
          user={post.author}
          username={post.author.username}
        />
        <Box sx={{ display: "flex", flexFlow: "column" }}>
          <Box sx={{ display: "flex" }}>
            {!post.author.username || post.author.deletedAccount ? (
              <Typography
                sx={{
                  p: 0,
                  px: 1,
                }}
              >
                {t("deletedUser")}
              </Typography>
            ) : (
              <User
                username={post.author.username}
                userId={post.authorID}
                showMessage={true}
              />
            )}
            {post.createdAt !== post.updatedAt && (
              <Chip size="small" label={t("edited")} />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ ml: 1, mr: 0.5 }} variant="p" fontSize="small">
              {DateTime.fromISO(post.createdAt)
                .setLocale(language)
                .toRelative()}
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center" }}
              fontSize="small"
            >
              &#183;
            </Box>
            <Box
              sx={{ display: "flex", alignItems: "center", ml: 0.5 }}
              fontSize="small"
            >
              {post.isPrivate ? (
                <LockIcon fontSize="0.5rem" />
              ) : (
                <PublicIcon fontSize="0.5rem" />
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ ml: "auto" }}>
          <IconButton
            aria-label={t("more")}
            id="long-button"
            aria-controls={open ? "long-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-haspopup="true"
            onClick={handleClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="long-menu"
            MenuListProps={{
              "aria-labelledby": "long-button",
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {post.authorID === profile?._id && (
              <MenuItem
                onClick={() => {
                  editCurrentPost();
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">{t("editPost")}</Typography>
              </MenuItem>
            )}
            {post.authorID === profile?._id && (
              <MenuItem
                onClick={() => {
                  deletePost(post._id);
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">{t("deletePost")}</Typography>
              </MenuItem>
            )}
            {profile?._id && (
              <MenuItem
                onClick={() => {
                  setIsReportPostModal((prev) => !prev);
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <SmsFailedOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">{t("reportPost")}</Typography>
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
      <PostDirectory post={post} />
      <Typography sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
        {segments.map((segment) =>
          segment.isLink ? (
            <Link
              key={segment.text}
              href={segment.url}
              target="_blank"
              sx={{ wordBreak: "break-all" }}
            >
              {segment.text}
            </Link>
          ) : (
            <Typography key={segment.text} component="span">
              {segment.text}
            </Typography>
          )
        )}
      </Typography>
      {links?.map((link) => (
        <LinkPreview key={link} url={link} />
      ))}
      {/* Custom CSS grid for images with expanded image spanning 2 columns and reordering */}
      {selectedImage ? (
        <div className="grid grid-cols-2 gap-2">
          {/* Expanded image at the top, spanning 2 columns */}
          <div className="col-span-2">
            <div
              className="relative"
              ref={(el) => {
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
              }}
              onClick={() => setSelectedImage(null)}
            >
              <img
                className="rounded-md transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer w-full h-auto border-2 border-blue-600 shadow-2xl"
                srcSet={
                  post.images.find((img) => img.key === selectedImage)?.url
                }
                src={post.images.find((img) => img.key === selectedImage)?.url}
                alt={t("post")}
                loading="lazy"
              />
            </div>
          </div>
          {/* Other images below, each in a single column */}
          {post.images
            .filter((img) => img.key !== selectedImage)
            .map((img) => (
              <div
                key={img.key}
                className="col-span-1"
                onClick={() => setSelectedImage(img.key)}
              >
                <img
                  className="rounded-md transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer w-full h-auto"
                  srcSet={img.url}
                  src={img.url}
                  alt={t("post")}
                  loading="lazy"
                />
              </div>
            ))}
        </div>
      ) : (
        <div
          className={`grid gap-2 ${
            post.images.length > 1 ? "grid-cols-2" : "grid-cols-1"
          }`}
        >
          {post.images.map((img) => (
            <div
              key={img.key}
              className="w-full"
              onClick={() => setSelectedImage(img.key)}
            >
              <img
                className="rounded-md transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer w-full h-auto"
                srcSet={img.url}
                src={img.url}
                alt={t("post")}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      )}
      <ReactionAndComments
        post={post}
        profile={profile}
        setIsCommentSectionOpened={setIsCommentSectionOpened}
        interactionDisabled={interactionDisabled}
      />
      {isCommentSectionOpened && (
        <CommentSection
          post={post}
          profile={profile}
          interactionDisabled={interactionDisabled}
        />
      )}
      {isReportPostModal && (
        <ReportPost
          profile={profile}
          post={post}
          isReportPostModal={isReportPostModal}
          setIsReportPostModal={setIsReportPostModal}
        />
      )}
    </Paper>
  );
}
